<template>
  <div>
    <v-progress-linear v-if="cargando" indeterminate color="purple" class="mb-2" />

    <ag-grid-vue
      :gridOptions="gridOptions"
      class="ag-theme-balham ag-grid-listado-inscripciones"
      :columnDefs="columnDefs"
      :defaultColDef="defaultColDef"
      @grid-ready="onGridReady"
      :rowData="rowData"
      rowSelection="multiple"
      colResizeDefault="shift"
      :animateRows="false"
      :floatingFilter="true"
      :pagination="false"
      :rowClassRules="rowClassRules"
      @selection-changed="onSelectionChanged"
      @first-data-rendered="onFirstDataRendered"
      :alwaysShowVerticalScroll="true"
      :suppressPaginationPanel="true"
      :isRowSelectable="isRowSelectable">
    </ag-grid-vue>
  </div>
</template>
  
<script>
import listadoAgGridMixin from '../mixins/listado-ag-grid.mixin';
import {
  COLORES_ESTADOS_ADMINISTRATIVOS,
  COLORES_ESTADOS_CARTERA,
  ESTADOS_ACADEMICOS,
  ESTADOS_ADMINISTRATIVOS,
  ESTADOS_CARTERA,
  ESTADOS_INSCRIPCION,
  FASES_PROGRAMA,
} from '../utils/constantes';
import {
  cellRendererNumeroRegistro,
  cellRendererSiONo,
  formatearValorAMoneda,
  imprimirProgramaModulo,
  obtenerInscripcionModuloMasAlto,
} from '../utils/funciones';
import BotonHistorialEstadosAcademicos from './BotonHistorialEstadosAcademicos.vue';
import configuracionesMixin from '../mixins/configuraciones.mixin';
import BotonModuloActual from './BotonModuloActual.vue';
import BotonMontoTotalConceptos from './BotonMontoTotalConceptos.vue';
import columnasMontosMixin from '../mixins/columnas-montos.mixin';

export default {
  name: 'ListadoInscripciones',
  mixins: [listadoAgGridMixin, configuracionesMixin, columnasMontosMixin],
  components: {
    // eslint-disable-next-line vue/no-unused-components
    BotonHistorialEstadosAcademicos,
    // eslint-disable-next-line vue/no-unused-components
    BotonModuloActual,
    // eslint-disable-next-line vue/no-unused-components
    BotonMontoTotalConceptos,
  },
  props: {
    itemsYaSeleccionados: {
      type: Array,
      required: true,
    },
    itemsYaSolicitados: {
      type: Array,
      default: () => [],
    },
    mostrarColumnaSolicitado: {
      type: Boolean,
      default: false,
    },
    itemsYaInscritos: {
      type: Array,
      default: () => [],
    },
    mostrarColumnaEstadoInscripcion: {
      type: Boolean,
      default: true,
    },
    universidadId: {
      type: Number,
      default: null,
    },
  },
  mounted() {
    const baseRowSelectable = (params) => {
      return (
        !!params.data &&
        !!params.data.matriculaPagada &&
        !!params.data.primeraCuotaPagada &&
        !this.yaSeleccionados.some((item) => item.inscripcionId === params.data.id) &&
        !this.itemsYaInscritos.some((item) => item.inscripcionId === params.data.id) &&
        params.data.estadoInscripcion == 1 &&
        params.data.estadoCartera?.nombre !== ESTADOS_CARTERA['Liquidado - Exento de deuda'] &&
        params.data.estadoAdministrativo?.nombre === ESTADOS_ADMINISTRATIVOS.Inscrito &&
        [
          ESTADOS_ACADEMICOS['Concluido Aprobado'],
          ESTADOS_ACADEMICOS['Aprobado Trabajo Final'],
          ESTADOS_ACADEMICOS['En Desarrollo'],
          ESTADOS_ACADEMICOS['Presentado'],
          ESTADOS_ACADEMICOS['En Desarrollo - Nivelacion'],
          ESTADOS_ACADEMICOS['Grupo'],
        ].includes(params.data.estadoAcademico)
      );
    };

    if (!this.universidadId) {
      this.isRowSelectable = baseRowSelectable;
    }

    this.obtenerConfiguraciones(this.universidadId).then(() => {
      const numeroModuloMinimoParaInscripcionUniversidad =
        this.configuraciones?.numeroModuloMinimoParaInscripcionUniversidad;

      this.isRowSelectable = (params) => {
        const inscripcionesModulos = params.data?.inscripcionesModulos;
        const inscripcionModuloMasAlto = obtenerInscripcionModuloMasAlto(inscripcionesModulos);

        return (
          baseRowSelectable(params) &&
          (!numeroModuloMinimoParaInscripcionUniversidad ||
            inscripcionModuloMasAlto?.programaModulo?.numero >= numeroModuloMinimoParaInscripcionUniversidad)
        );
      };
    });
  },
  watch: {
    mostrarColumnaSolicitado: {
      immediate: true,
      handler(value) {
        if (value) {
          this.agregarColumnaSolicitado();
        } else {
          this.columnDefs = this.columnDefs.filter((column) => column.field !== 'solicitado');
        }

        this?.agGridApi?.setGridOptions('columnDefs', this.columnDefs);
      },
    },
    mostrarColumnaEstadoInscripcion: {
      immediate: true,
      handler(value) {
        if (value) {
          this.agregarColumnaEstadoInscripcion();
        } else {
          this.columnDefs = this.columnDefs.filter((column) => column.field !== 'estadoInscripcion');
        }

        this?.agGridApi?.setGridOptions('columnDefs', this.columnDefs);
      },
    },
    universidadId: {
      immediate: true,
      handler(value) {
        if (value == this.universidadIdSigloXX) {
          this.agregarColumnaPrimeraCuotaPagada();
        } else {
          this.columnDefs = this.columnDefs.filter((column) => column.field !== 'primeraCuotaPagada');
        }

        if (value == this.universidadIdUnivPando) {
          this.quitarColumnasPagos();
        } else {
          this.agregarColumnasPagos();
        }

        this.columnDefs = this.columnDefs.map((column) => {
          if (column.field === 'matriculaPagada') {
            return {
              ...column,
              headerName: value == this.universidadIdSigloXX ? 'MATRÍCULA PAGADA' : 'PRIMERA CUOTA PAGADA',
            };
          }

          return column;
        });

        this?.agGridApi?.setGridOptions('columnDefs', this.columnDefs);
      },
    },
  },
  computed: {
    rowData() {
      return this.items.map((item) => ({
        ...item,
        solicitado: this.itemsYaSolicitados.some((solicitado) => solicitado.inscripcionId === item.id),
        inscrito: this.itemsYaInscritos.some((inscrito) => inscrito.inscripcionId === item.id),
      }));
    },
    yaSeleccionados() {
      return [...this.itemsYaSeleccionados, ...this.itemsYaSolicitados];
    },
  },
  data() {
    return {
      universidadIdSigloXX: 9,
      universidadIdUnivPando: 49,
      rowClassRules: {
        // 'row-pre-inscrito': `data.estadoInscripcion == '0'`,
        // 'row-inscrito': `data.estadoInscripcion == '1'`,
        // 'row-retirado': `data.estadoInscripcion == '2'`,
        // 'row-cambiado': `data.estadoInscripcion == '3'`,
        // 'row-congelado': `data.estadoInscripcion == '4'`,
        // 'row-pagado': `data.estadoInscripcion == '5'`,
      },
      isRowSelectable: null,
      columnDefs: [
        {
          headerName: 'N° REGISTRO',
          cellRenderer: cellRendererNumeroRegistro,
          filter: false,
          checkboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true,
          headerCheckboxSelection: true,
          minWidth: 130,
          maxWidth: 130,
        },
        {
          headerName: 'N° DOCUMENTO',
          field: 'estudiante.numeroDocumento',
          minWidth: 120,
          maxWidth: 120,
        },
        {
          headerName: 'ESTADO INSCRIP.',
          cellRenderer: (params) => {
            const estadoInscripcion = params.data?.estadoInscripcion;
            const estado = ESTADOS_INSCRIPCION[estadoInscripcion];
            let color = 'black';

            if (estadoInscripcion == 1) {
              color = 'green';
            } else if (estadoInscripcion == 2) {
              color = 'red';
            } else {
              color = 'blue';
            }

            return /*html*/ `
              <span class="v-chip v-chip--outlined v-chip--pill theme--dark v-size--x-small ${color} ${color}--text body-2">
                  <span class="v-chip__content">
                    ${estado}
                  </span>
              </span>
            `;
          },
          valueGetter: (params) => {
            const estadoInscripcion = params?.data?.estadoInscripcion;

            return ESTADOS_INSCRIPCION[estadoInscripcion];
          },
          minWidth: 140,
          maxWidth: 140,
        },
        {
          headerName: 'MATRÍCULA O PRIMERA CUOTA PAGADA',
          field: 'matriculaPagada',
          cellRenderer: cellRendererSiONo('matriculaPagada', 'SI', 'NO'),
          filter: false,
          minWidth: 180,
          maxWidth: 180,
        },
        {
          headerName: 'ESTADO DE CARTERA',
          cellRenderer: (params) => {
            const estadoCartera = params.data?.estadoCartera.nombre;

            if (!estadoCartera) {
              return '-';
            }

            const color = COLORES_ESTADOS_CARTERA[estadoCartera] || 'black';

            return /*html*/ `
                  <span class="v-chip v-chip--outlined v-chip--pill theme--dark v-size--x-small ${color} ${color}--text body-2">
                      <span class="v-chip__content">
                        ${estadoCartera}
                      </span>
                  </span>
                `;
          },
          filter: true,
          valueGetter: (params) => {
            const estadoCartera = params.data?.estadoCartera.nombre;

            return estadoCartera;
          },
          minWidth: 220,
          maxWidth: 220,
        },
        {
          headerName: 'ESTADO ADMINISTRATIVO',
          cellRenderer: (params) => {
            const estadoAdministrativo = params.data?.estadoAdministrativo.nombre;

            if (!estadoAdministrativo) {
              return '-';
            }

            const color = COLORES_ESTADOS_ADMINISTRATIVOS[estadoAdministrativo] || 'black';

            return /*html*/ `
                  <span class="v-chip v-chip--outlined v-chip--pill theme--dark v-size--x-small ${color} ${color}--text body-2">
                      <span class="v-chip__content">
                        ${estadoAdministrativo}
                      </span>
                  </span>
                `;
          },
          filter: true,
          valueGetter: (params) => {
            const estadoAdministrativo = params.data?.estadoAdministrativo.nombre;

            if (!estadoAdministrativo) {
              return '-';
            }

            return estadoAdministrativo;
          },
          minWidth: 180,
          maxWidth: 180,
        },
        {
          headerName: 'ESTADO ACADÉMICO',
          cellRenderer: 'BotonHistorialEstadosAcademicos',
          valueGetter: (params) => {
            const estadoAcademico = params?.data?.estadoAcademico || '-';

            return estadoAcademico;
          },
          filter: true,
          minWidth: 250,
          maxWidth: 250,
        },
        {
          headerName: 'MÓDULO ACTUAL',
          cellRenderer: 'BotonModuloActual',
          filter: true,
          valueGetter: (params) => {
            const inscripcionesModulos = params.data?.inscripcionesModulos;
            const inscripcionModuloMasAlto = obtenerInscripcionModuloMasAlto(inscripcionesModulos);

            return imprimirProgramaModulo(inscripcionModuloMasAlto);
          },
          minWidth: 200,
          maxWidth: 200,
        },
        {
          headerName: 'ES REGULARIZADO',
          field: 'esRegularizado',
          cellRenderer: cellRendererSiONo('esRegularizado', 'SI', 'NO'),
          filter: false,
          minWidth: 130,
        },
        {
          headerName: 'PRIMER APELLIDO',
          field: 'estudiante.primerApellido',
          minWidth: 180,
        },
        {
          headerName: 'SEGUNDO APELLIDO',
          field: 'estudiante.segundoApellido',
          minWidth: 180,
        },
        {
          headerName: 'NOMBRES',
          field: 'estudiante.nombres',
          minWidth: 200,
        },
        {
          headerName: 'ID PROGRAMA',
          field: 'programa.id',
          minWidth: 120,
          maxWidth: 120,
        },
        {
          headerName: 'PROGRAMA',
          field: 'programa.nombreCompuesto',
          minWidth: 400,
        },
        {
          headerName: 'FASE',
          field: 'programa.fase',
          minWidth: 200,
          maxWidth: 200,
          valueGetter: (params) => {
            return FASES_PROGRAMA[params.data.programa.fase] || 'DESCONOCIDO';
          },
        },
        {
          headerName: 'SEDE',
          field: 'programa.sede.nombre',
          minWidth: 160,
          maxWidth: 160,
        },
        {
          headerName: 'GÉNERO',
          cellRenderer: (params) => {
            const genero = params?.data?.estudiante?.sexo;

            return genero === 'M' ? 'MASCULINO' : 'FEMENINO';
          },
          filter: false,
          minWidth: 100,
          maxWidth: 100,
        },
        {
          headerName: 'CELULAR',
          cellRenderer: (params) => {
            const estudiante = params?.data?.estudiante;
            const celular = estudiante?.telefono1 || estudiante?.telefono2;

            return celular || '-';
          },
          minWidth: 120,
          maxWidth: 120,
        },
        {
          headerName: 'CORREO',
          cellRenderer: (params) => {
            const correo = params?.data?.estudiante?.email;

            return correo || '-';
          },
          minWidth: 250,
        },
        {
          headerName: 'PROFESIÓN',
          cellRenderer: (params) => {
            const profesiones = params?.data?.estudiante?.profesiones;

            return profesiones?.length ? profesiones[0].nombre : '-';
          },
          minWidth: 250,
        },
        {
          headerName: 'UNIV. TITULACIÓN',
          cellRenderer: (params) => {
            const universidad = params?.data?.estudiante?.institucion?.nombre;

            return universidad || '-';
          },
          minWidth: 450,
        },
      ],
      columnasPagos: [
        {
          headerName: 'PAGO MÁTRICULA',
          cellRenderer: (params) => {
            const montos = params.data?.montos;

            if (!montos) {
              return '-';
            }

            return formatearValorAMoneda(montos?.pagoMatricula);
          },
          filter: false,
          minWidth: 150,
          maxWidth: 150,
          type: 'numericColumn',
        },
        {
          headerName: 'PAGO COLEGIATURA',
          cellRenderer: (params) => {
            const montos = params.data?.montos;

            if (!montos) {
              return '-';
            }

            return formatearValorAMoneda(montos?.pagoColegiatura);
          },
          filter: false,
          minWidth: 150,
          maxWidth: 150,
          type: 'numericColumn',
        },
        {
          headerName: 'PAGO CERTIFICACIÓN',
          cellRenderer: (params) => {
            const montos = params.data?.montos;

            if (!montos) {
              return '-';
            }

            return formatearValorAMoneda(montos?.pagoCertificacion);
          },
          filter: false,
          minWidth: 150,
          maxWidth: 150,
          type: 'numericColumn',
        },
        {
          headerName: 'MONTO TOTAL PAGADO',
          cellRenderer: 'BotonMontoTotalConceptos',
          filter: false,
          minWidth: 200,
          maxWidth: 200,
          type: 'numericColumn',
        },
      ],
    };
  },
  methods: {
    onSelectionChanged() {
      this.selectedRows = this.gridApi.getSelectedRows();
      this.$emit('selectItem', this.selectedRows);
    },
    agregarColumnaSolicitado() {
      this.columnDefs.splice(3, 0, {
        headerName: 'SOLICITADO',
        field: 'solicitado',
        cellRenderer: cellRendererSiONo('solicitado', 'SI', 'NO'),
        filter: false,
        minWidth: 100,
        maxWidth: 100,
      });
    },
    agregarColumnaEstadoInscripcion() {
      this.columnDefs.splice(3, 0, {
        headerName: 'INSCRIP. PROGRAMA UNIV.',
        cellRenderer: cellRendererSiONo('inscritoUniversidad', 'SI', 'NO'),
        valueGetter: (params) => {
          return params?.data?.inscritoUniversidad ? 'SI' : 'NO';
        },
        filter: true,
        minWidth: 180,
        maxWidth: 180,
      });
    },
    agregarColumnaPrimeraCuotaPagada() {
      let indice = 3;

      if (this.mostrarColumnaSolicitado) {
        indice++;
      }

      if (this.mostrarColumnaEstadoInscripcion) {
        indice++;
      }

      this.columnDefs.splice(indice, 0, {
        headerName: 'PRIMERA CUOTA PAGADA',
        field: 'primeraCuotaPagada',
        cellRenderer: cellRendererSiONo('primeraCuotaPagada', 'SI', 'NO'),
        filter: false,
        minWidth: 180,
        maxWidth: 180,
      });
    },
  },
};
</script>
  
<style>
@import '~ag-grid-community/dist/styles/ag-grid.css';
@import '~ag-grid-community/dist/styles/ag-theme-balham.css';

.ag-grid-listado-inscripciones {
  height: calc(var(--vh, 1vh) * 100 - 22rem);
}

.ag-theme-balham .row-pre-inscrito {
  color: #929292 !important;
}

.ag-theme-balham .row-inscrito {
  color: #309c51 !important;
}

.ag-theme-balham .row-retirado {
  color: #c41111 !important;
}
</style>
  